import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { Sparkles, Download, Loader2, RefreshCw, AlertTriangle, Lock, FileSpreadsheet, Search, ChevronDown, ChevronRight, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Loading messages to cycle through during generation
const LOADING_MESSAGES = [
  'Analyzing requirements...',
  'Generating test scenarios...',
  'Creating test cases...',
  'Building test plan...',
  'Finalizing QA documentation...',
  'Almost there...',
];

export const TestCasesTab = ({ projectId, testCases, requirements, testRuns = [], onUpdate, usage }) => {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingJira, setExportingJira] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const [qaJobId, setQaJobId] = useState(null);
  const [qaProgress, setQaProgress] = useState(0);
  const [scenarioMap, setScenarioMap] = useState({});
  const loadingIntervalRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (projectId) fetchScenarioMap();
  }, [projectId]);

  const fetchScenarioMap = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/scenarios`);
      const map = {};
      (res.data || []).forEach(s => { map[s.scenario_id] = s; });
      setScenarioMap(map);
    } catch { /* ignore */ }
  };
  const pollIntervalRef = useRef(null);
  const jobCompletedRef = useRef(false);
  const backgroundNoticeShownRef = useRef(false);
  const [backgroundRunning, setBackgroundRunning] = useState(false);

  // Enterprise-safe: Check if test runs exist
  const hasTestRuns = testRuns && testRuns.length > 0;
  const testRunsCount = testRuns?.length || 0;

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Auto-resume: on mount / project change, re-attach to any in-flight QA job so the
  // user doesn't have to click "Generate" again after navigating away and back.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/projects/${projectId}/qa-jobs/active`);
        if (cancelled) return;
        if (res.data?.active && res.data?.job_id) {
          const jobType = res.data.job_type || 'generate';
          if (jobType === 'regenerate') {
            setRegenerating(true);
          } else {
            setGenerating(true);
          }
          setQaProgress(res.data.progress || 0);
          setQaJobId(res.data.job_id);
        }
      } catch {
        /* no active job or transient error — ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Polling effect for async QA generation/regeneration
  useEffect(() => {
    let pollCount = 0;
    // Soft threshold: after ~6 min we stop calling the job "in-progress" in the UI
    // and instead surface a non-destructive "still running in background" notice.
    // The backend keeps working; we keep polling.
    const softNoticeCount = 120; // 120 * 3s = 6 min
    // Hard safety upper bound so the interval doesn't run forever if the tab is left open
    // and the job silently dies. Well above realistic generation times (60 min).
    const hardMaxPollCount = 1200; // 1200 * 3s = 60 min

    // Clear any existing interval before starting new one
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (qaJobId && (generating || regenerating)) {
      // Reset flags when starting/attaching to a job
      jobCompletedRef.current = false;
      backgroundNoticeShownRef.current = false;
      setBackgroundRunning(false);
      
      pollIntervalRef.current = setInterval(async () => {
        // Guard: Skip if already completed
        if (jobCompletedRef.current) {
          return;
        }
        
        try {
          pollCount++;
          const response = await api.get(`/qa-status/${qaJobId}`);
          const data = response.data;
          
          // Double-check guard after async call
          if (jobCompletedRef.current) {
            return;
          }
          
          setQaProgress(data.progress || 0);
          // Show clean user-friendly message without internal chunk counts
          setLoadingMessage(regenerating ? 'Regenerating test cases...' : 'Generating test cases...');
          
          if (data.status === 'completed') {
            // Mark as completed FIRST to prevent duplicate processing
            if (jobCompletedRef.current) {
              return; // Already handled
            }
            jobCompletedRef.current = true;
            
            // Clear interval immediately
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            
            // Update state
            setGenerating(false);
            setRegenerating(false);
            setQaJobId(null);
            setQaProgress(0);
            setBackgroundRunning(false);
            
            // Show success toast ONCE
            toast.success(data.message || 'QA Pack generated successfully!');
            onUpdate();
            
          } else if (data.status === 'failed') {
            // Mark as completed to prevent further processing
            jobCompletedRef.current = true;
            
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            
            setGenerating(false);
            setRegenerating(false);
            setQaJobId(null);
            setQaProgress(0);
            setBackgroundRunning(false);
            
            toast.error(data.error_message || 'QA generation failed');
            
          } else if (pollCount === softNoticeCount && !backgroundNoticeShownRef.current) {
            // Soft threshold reached: job is still healthy on the backend — do NOT
            // treat this as a failure and do NOT clear qaJobId. Show a one-time
            // non-destructive banner + toast; keep polling.
            backgroundNoticeShownRef.current = true;
            setBackgroundRunning(true);
            toast.info(
              'Generation is still running in the background. You can safely leave this page while TestGenie continues processing.',
              { duration: 10000 }
            );
          } else if (pollCount >= hardMaxPollCount) {
            // Hard safety cap: something is very wrong. Stop polling but stay
            // non-destructive — the backend job doc will still be visible on reload.
            jobCompletedRef.current = true;
            
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            
            setGenerating(false);
            setRegenerating(false);
            setQaJobId(null);
            setQaProgress(0);
            setBackgroundRunning(false);
            
            toast.info('Still running in the background. Reopen the project later to see results.');
          }
        } catch (error) {
          console.error('QA polling error:', error);
          // Don't stop polling on network errors
        }
      }, 3000); // Poll every 3 seconds
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [qaJobId, generating, regenerating, onUpdate]);

  // Cycle through loading messages during generation (fallback)
  useEffect(() => {
    if ((generating || regenerating) && !qaJobId) {
      let messageIndex = 0;
      loadingIntervalRef.current = setInterval(() => {
        messageIndex = (messageIndex + 1) % LOADING_MESSAGES.length;
        setLoadingMessage(LOADING_MESSAGES[messageIndex]);
      }, 8000);
    } else if (!generating && !regenerating) {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
      setLoadingMessage(LOADING_MESSAGES[0]);
    }
    
    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
    };
  }, [generating, regenerating, qaJobId]);

  // Force an immediate status refresh (used by the "Check now" button when the job
  // has passed the soft 6-min threshold). Polling continues in parallel — this just
  // fires an out-of-band fetch so the user gets instant feedback.
  const handleCheckStatusNow = async () => {
    if (!qaJobId) return;
    try {
      const res = await api.get(`/qa-status/${qaJobId}`);
      const data = res.data;
      setQaProgress(data.progress || 0);
      if (data.status === 'completed') {
        jobCompletedRef.current = true;
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setGenerating(false);
        setRegenerating(false);
        setQaJobId(null);
        setQaProgress(0);
        setBackgroundRunning(false);
        toast.success(data.message || 'QA Pack generated successfully!');
        onUpdate();
      } else if (data.status === 'failed') {
        jobCompletedRef.current = true;
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setGenerating(false);
        setRegenerating(false);
        setQaJobId(null);
        setQaProgress(0);
        setBackgroundRunning(false);
        toast.error(data.error_message || 'QA generation failed');
      } else {
        toast.info(`Still ${data.phase || 'working'}... (${data.progress || 0}%)`);
      }
    } catch {
      toast.error('Could not fetch job status. Please try again.');
    }
  };

  const handleGenerateQAPack = async () => {
    if (requirements.length === 0) {
      toast.error('Please add requirements first');
      return;
    }
    
    setGenerating(true);
    setQaProgress(0);
    setLoadingMessage('Starting AI generation...');
    
    try {
      const response = await api.post(`/projects/${projectId}/generate-qa-pack`, {}, {
        timeout: 30000 // 30 second timeout for initial request
      });
      
      const data = response.data;
      
      // Check if all scenarios already covered
      if (data.already_covered) {
        setGenerating(false);
        toast.info(data.message || 'All scenarios already have test cases', { duration: 6000 });
        return;
      }
      
      // Check if async mode
      if (data.async && data.job_id) {
        // Start polling
        setQaJobId(data.job_id);
        const hasExisting = testCases.length > 0;
        toast.info(hasExisting ? 'Generating test cases for new scenarios only...' : 'QA generation started. This may take 1-2 minutes.');
      } else if (data.test_cases_generated > 0) {
        // Synchronous response (backward compatibility)
        setGenerating(false);
        toast.success(data.message || 'QA Pack generated successfully!');
        toast.info(`Generated ${data.test_cases_generated} new test case(s)`);
        onUpdate();
      } else {
        setGenerating(false);
        toast.info(data.message || 'No new test cases generated.');
        onUpdate();
      }
      
    } catch (error) {
      setGenerating(false);
      setQaJobId(null);
      
      if (error.response) {
        toast.error(error.response.data?.detail || 'Failed to generate QA pack');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.warning('Request timed out. Please try again.');
      } else {
        toast.error('Failed to start QA generation. Please try again.');
      }
    }
  };

  const handleRegenerateAll = async () => {
    setRegenerating(true);
    setQaProgress(0);
    setLoadingMessage('Starting regeneration...');
    
    try {
      const response = await api.post(`/projects/${projectId}/regenerate-qa-pack`, {}, {
        timeout: 30000 // 30 second timeout for initial request
      });
      
      const data = response.data;
      
      // Handle blocked regeneration (test runs exist)
      if (data.blocked) {
        setRegenerating(false);
        toast.error(data.message, { duration: 6000 });
        setShowRegenerateDialog(false);
        return;
      }
      
      // Check if async mode
      if (data.async && data.job_id) {
        // Start polling (reuse same qaJobId state as generate)
        setQaJobId(data.job_id);
        toast.info('Regeneration started. This may take 1-2 minutes.');
        setShowRegenerateDialog(false);
      } else if (data.test_cases_generated !== undefined) {
        // Synchronous response (backward compatibility)
        setRegenerating(false);
        toast.success(data.message || 'QA Pack regenerated successfully!');
        setShowRegenerateDialog(false);
        onUpdate();
      }
      
    } catch (error) {
      setRegenerating(false);
      setQaJobId(null);
      
      // Handle enterprise-safe block message
      if (error.response?.status === 400) {
        const errData = error.response.data;
        if (errData?.blocked || errData?.error === 'Regeneration blocked') {
          toast.error(errData.message || errData.detail || 'Regeneration blocked', { duration: 6000 });
        } else {
          toast.error(errData?.detail || errData?.message || 'Failed to regenerate');
        }
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.warning('Request timed out. Please try again.');
      } else {
        toast.error('Failed to start regeneration. Please try again.');
      }
      setShowRegenerateDialog(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/export/testcases/${projectId}`, { responseType: 'blob' });
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'testcases.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)$/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Test cases exported successfully');
    } catch (error) {
      toast.error('Failed to export test cases');
    } finally {
      setExporting(false);
    }
  };

  const handleExportJiraCsv = async () => {
    setExportingJira(true);
    try {
      const response = await api.get(`/export/testcases/${projectId}/jira-csv`, { responseType: 'blob' });
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'jira_zephyr_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)$/);
        if (filenameMatch && filenameMatch[1]) filename = filenameMatch[1].replace(/['"]/g, '');
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export successful for Jira (compatible with Zephyr)');
    } catch (error) {
      toast.error('Failed to export Jira CSV');
    } finally {
      setExportingJira(false);
    }
  };

  const isProcessing = generating || regenerating;
  const hasTestCases = testCases.length > 0;

  const tcLimitReached = usage?.testcases?.used >= usage?.testcases?.limit;

  // Frontend filtering
  const filteredTestCases = testCases.filter(tc => {
    if (searchQuery && !tc.scenario_title?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterPriority !== 'all' && tc.priority !== filterPriority) return false;
    if (filterType !== 'all' && tc.type !== filterType) return false;
    return true;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-semibold text-blueprint">Test Cases</h2>
        <div className="flex gap-2">
          {hasTestCases && (
            <>
              <Button variant="outline" onClick={handleExport} disabled={isProcessing || exporting} className="gap-2" data-testid="export-testcases-button">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {exporting ? 'Exporting...' : 'Export Excel'}
              </Button>
              <Button variant="outline" onClick={handleExportJiraCsv} disabled={isProcessing || exportingJira} className="gap-2" data-testid="export-jira-csv-button">
                {exportingJira ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                {exportingJira ? 'Exporting...' : 'Export for Jira (Zephyr Compatible)'}
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button 
                        variant="outline" 
                        onClick={() => setShowRegenerateDialog(true)} 
                        disabled={isProcessing || requirements.length === 0 || hasTestRuns}
                        className={`gap-2 ${hasTestRuns 
                          ? 'text-muted-foreground border-muted cursor-not-allowed' 
                          : 'text-orange-600 hover:text-orange-700 border-orange-200 hover:bg-orange-50'
                        }`}
                        data-testid="regenerate-all-button"
                      >
                        {hasTestRuns ? (
                          <Lock className="h-4 w-4" />
                        ) : regenerating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        {regenerating ? loadingMessage : 'Regenerate All'}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {hasTestRuns && (
                    <TooltipContent side="bottom" className="max-w-xs">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Regeneration Locked</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {testRunsCount} test run{testRunsCount > 1 ? 's' : ''} exist{testRunsCount === 1 ? 's' : ''} for this project. 
                            Test cases are baselined after execution to maintain traceability.
                          </p>
                        </div>
                      </div>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </>
          )}
          <Button 
            onClick={handleGenerateQAPack} 
            disabled={isProcessing || requirements.length === 0 || tcLimitReached} 
            className="gap-2 bg-blueprint hover:bg-blueprint/90 min-w-[200px]" 
            data-testid="generate-qa-pack-button"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="truncate">{loadingMessage}</span>
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                {hasTestCases ? 'Regenerate QA Pack' : 'Generate QA Pack'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Progress banner */}
      {isProcessing && qaJobId && (
        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg" data-testid="qa-progress-banner">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-700">{regenerating ? 'Regenerating test cases...' : 'Generating test cases...'}</p>
              {backgroundRunning && (
                <p className="text-xs text-blue-600 mt-1" data-testid="qa-background-notice">
                  Generation is still running in the background. You can safely leave this page while TestGenie continues processing.
                </p>
              )}
              <div className="mt-1.5 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${qaProgress}%` }} />
              </div>
            </div>
            <span className="text-xs font-mono text-blue-600">{qaProgress}%</span>
            {backgroundRunning && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleCheckStatusNow}
                className="text-xs h-7"
                data-testid="qa-check-now-button"
              >
                Check now
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Search and filters */}
      {hasTestCases && (
        <div className="flex gap-3 mb-4" data-testid="tc-search-filters">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search test cases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="tc-search-input"
            />
          </div>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-[140px]" data-testid="tc-filter-priority">
              <Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px]" data-testid="tc-filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Functional">Functional</SelectItem>
              <SelectItem value="Boundary">Edge</SelectItem>
              <SelectItem value="Negative">Negative</SelectItem>
              <SelectItem value="Security">Security</SelectItem>
              <SelectItem value="Validation">Validation</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {testCases.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="testcases-empty-state">
          <Sparkles className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-heading font-semibold mb-2 text-blueprint">No test cases yet</h3>
          <p className="text-muted-foreground mb-6">Generate test cases from your requirements using AI</p>
        </Card>
      ) : filteredTestCases.length === 0 ? (
        <Card className="p-8 text-center border-dashed" data-testid="testcases-no-results">
          <Search className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">No test cases match your filters</p>
          <Button variant="link" className="mt-2" onClick={() => { setSearchQuery(''); setFilterPriority('all'); setFilterType('all'); }}>Clear filters</Button>
        </Card>
      ) : (
        <TestCaseList testCases={filteredTestCases} scenarioMap={scenarioMap} navigate={navigate} />
      )}

      {showRegenerateDialog && (
        <Dialog open={showRegenerateDialog} onOpenChange={(open) => !regenerating && setShowRegenerateDialog(open)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Regenerate All Test Cases</DialogTitle>
              <DialogDescription>
                This will delete all existing test cases and regenerate them from scratch.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground mb-2">
                <strong>Warning:</strong> This action will:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Delete all {testCases.length} existing test case(s)</li>
                <li>Regenerate test cases for all requirements using AI</li>
                <li>Create new sequential test case IDs</li>
                <li>Remove all execution history for deleted test cases</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">
                This cannot be undone. Consider using "Generate QA Pack" for incremental updates instead.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowRegenerateDialog(false)}
                disabled={regenerating}
                data-testid="cancel-regenerate-button"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRegenerateAll}
                disabled={regenerating}
                className="gap-2"
                data-testid="confirm-regenerate-button"
              >
                {regenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {loadingMessage}
                  </>
                ) : (
                  'Regenerate All'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const TestCaseCard = ({ tc, navigate }) => (
  <Card key={tc.id} className="p-5 hover:border-azure/50 transition-all cursor-pointer" onClick={() => navigate(`/testcases/${tc.id}`)} data-testid={`testcase-card-${tc.tc_id}`}>
    <div className="flex items-center gap-2 flex-wrap mb-2">
      <span className="px-2 py-0.5 bg-azure/10 text-azure rounded font-mono text-sm font-medium">{tc.tc_id}</span>
      <span className="px-2 py-0.5 bg-muted rounded text-xs font-medium">{tc.module}</span>
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${tc.priority === 'High' ? 'bg-red-50 text-red-700 ring-1 ring-red-600/20' : tc.priority === 'Medium' ? 'bg-yellow-50 text-yellow-800 ring-1 ring-yellow-600/20' : 'bg-green-50 text-green-700 ring-1 ring-green-600/20'}`}>{tc.priority}</span>
      <span className="px-2 py-0.5 bg-accent rounded text-xs font-medium">{tc.type}</span>
      {tc.generated_by_ai && (
        <span className="px-2 py-0.5 bg-azure/10 text-azure rounded-full text-xs font-medium flex items-center gap-1">
          <Sparkles className="h-3 w-3" />AI
        </span>
      )}
    </div>
    <h3 className="text-base font-heading font-semibold text-blueprint">{tc.scenario_title}</h3>
    <p className="text-sm text-muted-foreground mt-1">{tc.steps.length} steps · {tc.expected_results.length} expected results</p>
  </Card>
);

const TestCaseList = ({ testCases, scenarioMap, navigate }) => {
  const [collapsed, setCollapsed] = useState({});

  const toggle = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  // Separate linked and unlinked test cases
  const linked = testCases.filter(tc => tc.scenario_id && scenarioMap[tc.scenario_id]);
  const unlinked = testCases.filter(tc => !tc.scenario_id || !scenarioMap[tc.scenario_id]);

  // Group linked by scenario_id
  const groups = {};
  linked.forEach(tc => {
    if (!groups[tc.scenario_id]) groups[tc.scenario_id] = [];
    groups[tc.scenario_id].push(tc);
  });

  const hasGroups = Object.keys(groups).length > 0;

  return (
    <div className="space-y-6" data-testid="testcase-list">
      {hasGroups && Object.entries(groups).map(([scenarioId, tcs]) => {
        const scenario = scenarioMap[scenarioId];
        const isCollapsed = collapsed[scenarioId];
        const typeBadgeClass = {
          positive: 'bg-emerald-100 text-emerald-700',
          negative: 'bg-red-100 text-red-700',
          edge: 'bg-amber-100 text-amber-700',
          security: 'bg-violet-100 text-violet-700',
          validation: 'bg-blue-100 text-blue-700',
        }[scenario?.type] || 'bg-muted text-muted-foreground';

        return (
          <div key={scenarioId} data-testid={`scenario-group-${scenarioId}`}>
            <button
              onClick={() => toggle(scenarioId)}
              className="flex items-center gap-2 mb-2 pl-1 w-full text-left hover:bg-muted/50 rounded py-1 transition-colors"
              data-testid={`scenario-toggle-${scenarioId}`}
            >
              {isCollapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              <span className="font-mono text-xs text-muted-foreground">{scenarioId}</span>
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${typeBadgeClass}`}>{scenario?.type}</span>
              <span className="text-sm font-medium text-slate-700 flex-1">{scenario?.title}</span>
              <span className="text-xs text-muted-foreground">{tcs.length} TC{tcs.length !== 1 ? 's' : ''}</span>
            </button>
            {!isCollapsed && (
              <div className="space-y-3 ml-3 pl-3 border-l-2 border-azure/20">
                {tcs.map(tc => <TestCaseCard key={tc.id} tc={tc} navigate={navigate} />)}
              </div>
            )}
          </div>
        );
      })}

      {unlinked.length > 0 && (
        <div data-testid="unlinked-testcases">
          {hasGroups && <h4 className="text-sm font-medium text-muted-foreground mb-3 mt-4">Other Test Cases</h4>}
          <div className="space-y-3">
            {unlinked.map(tc => <TestCaseCard key={tc.id} tc={tc} navigate={navigate} />)}
          </div>
        </div>
      )}
    </div>
  );
};